// React
import { ReactNode } from "react"

// Translations
import { useTrans } from "@/i18n"

// UI
import { PageTemplate, PageTemplateLink } from "@/layouts/PageTemplate"
import { Tooltip } from "@/components/Tooltip"

// Icons
import { FiInfo } from "@/lib/icons"

// Hooks
import { useCurrentProject } from "./hooks"

// Pages
import { Routes } from "@/constants/routes"

import { ProjectStateEnum } from "@/api/graphql"

// Feature flags
import { useFeatureFlags } from "@/context/user"

/**
 * ProjectLayout
 * @param {ReactNode} children
 * @returns
 */
export const ProjectLayout = ({ children }: { children: ReactNode }) => {
	const { getFeatureFlagValue } = useFeatureFlags()
	const { data } = useCurrentProject()
	const t = useTrans("project")

	return (
		<>
			<title>
				{t("common:web.title", {
					page: data?.project?.name,
				})}
			</title>
			<PageTemplate
				title={data?.project?.name ?? ""}
				backHref={Routes.InvestmentsProjects}
				tabs={
					<>
						<PageTemplateLink
							to={`${Routes.InvestmentsProjects}/${data?.project?.id}/dashboard`}
							end
							data-testid="project:dashboard"
							name={t("project:dashboard.title")}
							analyticsId="project:dashboard"
						/>
						<PageTemplateLink
							to={`${Routes.InvestmentsProjects}/${data?.project?.id}/revenue`}
							data-testid="project:revenue"
							name={t("project:revenue.title")}
							analyticsId="project:revenue"
						/>

						{/** Project Statistics */}
						<PageTemplateLink
							to={Routes.StatisticsPlatformSingleProject.replace(
								":id",
								data?.project?.id ?? "",
							)}
							name={t(
								"common:navigation.statistics.platform.link",
							)}
							data-testid="project:stats"
							analyticsId="project:stats"
							badge={{
								type: "element",
								element: (
									<Tooltip
										content={
											<p>{t("project:revenue.stats")}</p>
										}
									>
										<span className="flex items-center">
											<FiInfo className="ml-1" />
										</span>
									</Tooltip>
								),
							}}
						/>

						{/** Show production when project has Production data */}
						{data?.project?.state ===
						ProjectStateEnum.InProduction ? (
							<PageTemplateLink
								to={`${Routes.InvestmentsProjects}/${data?.project?.id}/data`}
								data-testid="project:data"
								name={t("project:data.title")}
								analyticsId="project:data"
							/>
						) : null}

						<PageTemplateLink
							to={`${Routes.InvestmentsProjects}/${data?.project?.id}/notifications`}
							data-testid="project:notifications"
							name={t("project:notifications.title")}
							analyticsId="project:notifications"
						/>
						{getFeatureFlagValue(
							"FEATURE_ENABLE_PROJECT_TIMELINE",
						) === true && (
							<PageTemplateLink
								to={`${Routes.InvestmentsProjects}/${data?.project?.id}/communications`}
								data-testid="project:communications"
								name={t("project:communications.title")}
								analyticsId="project:communications"
							/>
						)}

						<PageTemplateLink
							to={`${Routes.InvestmentsProjects}/${data?.project?.id}/documentation`}
							data-testid="project:documentation"
							name={t("project:documentation.title")}
							analyticsId="project:documentation"
						/>
					</>
				}
			>
				{children}
			</PageTemplate>
		</>
	)
}
